import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CollaboraEditor } from '@/modules/collabora-editor/index.jsx';
import { fileKind } from '@/shared/utils/fileKind.js';
import { TextPreview } from '@/shared/ui/TextPreview.jsx';

export const FilePreview = ({ file, shareToken = null }) => {
  const { t } = useTranslation(['editor']);
  // One shared classifier (src/shared/utils/fileKind.js) decides how to render.
  // The Microsoft Office Online viewer is deliberately not used — it needs a
  // publicly reachable URL, which our self-hosted/authenticated files aren't.
  const kind = fileKind(file);
  const hasHtmlContent = file.htmlText || file.html_text;
  const filename = file.name;

  // Office formats render via Collabora (read-only) using our WOPI flow —
  // works for both authenticated files and share links (via shareToken).
  if (kind === 'office' && (file.id || shareToken)) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="h-[75vh] bg-[#f8f9fa] dark:bg-[#1e1e1e] overflow-hidden rounded-lg">
            <CollaboraEditor
              fileId={file.id}
              readonly={true}
              shareToken={shareToken}
              onReady={() => {}}
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Images: render directly in the browser.
  if (kind === 'image' && file.previewUrl) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-center bg-muted rounded-lg h-[75vh] p-4">
            <img src={file.previewUrl} alt={filename} className="max-w-full max-h-full object-contain" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // PDF: the browser renders it natively.
  if (kind === 'pdf' && file.previewUrl) {
    return (
      <Card>
        <CardContent className="p-0">
          <iframe
            src={file.previewUrl}
            className="w-full h-[75vh] rounded-lg"
            title={filename}
          />
        </CardContent>
      </Card>
    );
  }

  // Plain text: fetch + render inline.
  if (kind === 'text' && file.previewUrl) {
    return (
      <Card>
        <CardContent className="p-0">
          <TextPreview url={file.previewUrl} className="h-[75vh] rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  if (hasHtmlContent) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="bg-muted p-6 rounded-lg flex justify-center overflow-auto h-[75vh]">
            <div
              className="bg-white shadow-2xl border border-border flex-shrink-0"
              style={{
                width: '210mm',
                minHeight: '297mm',
                transform: 'scale(0.6)',
                transformOrigin: 'top center',
                marginBottom: '-40%',
              }}
            >
              <iframe
                srcDoc={file.htmlText || file.html_text}
                className="w-full border-0"
                style={{ minHeight: '297mm' }}
                title={filename}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center p-12 text-center">
        <FileText className="w-16 h-16 text-muted-foreground/40 mb-4" />
        <p className="text-muted-foreground">{t('editor:previewUnavailable')}</p>
        {file.previewUrl && (
          <Button asChild className="mt-4" variant="outline">
            <a href={file.previewUrl} download>
              {t('editor:downloadToView')}
            </a>
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
